import { useEffect, useState } from 'react';
import { useRecordContext, RaRecord } from 'react-admin';
import { Box, Button, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material';
import axiosNest from '../../api/axiosNest';
import { toErrorMessage } from '../userOffers/orderActionsApi';
import { SectionCard, fmtDate } from '../../shared/Panel';
import {
  carChangeFieldLabel,
  carChangeSourceLabel,
  carChangeValueText,
  type CarChangeRow,
} from './carChanges';

/**
 * „Historia zmian z TMS” na karcie pojazdu (12.10.2026, decyzja użytkownika
 * B2: „zapisuj”). Import kartoteki nadpisuje auto, więc przepięta naczepa
 * albo wyłączenie z eksploatacji nie zostawiały śladu — teraz każda zmiana
 * ma wiersz: kiedy (czas polski), pole, z czego na co i skąd (odczyt zmian
 * co godzinę, pełny odczyt nocny albo wpis ręczny). Sekcja zwinięta, dane
 * dociąga dopiero po rozwinięciu (`GET /cars/:id/changes`).
 */

interface CarChangesView {
  items: CarChangeRow[];
  limit: number;
}

export async function fetchCarChanges(carId: number | string): Promise<CarChangesView> {
  const { data } = await axiosNest.get(`/cars/${carId}/changes`);
  const view = (data ?? {}) as Partial<CarChangesView>;
  return {
    items: Array.isArray(view.items) ? view.items : [],
    limit: typeof view.limit === 'number' ? view.limit : 0,
  };
}

export const CarChangeHistory = () => {
  const r = useRecordContext<RaRecord & Record<string, unknown>>();
  const carId = r?.id;
  const [open, setOpen] = useState(false);
  const [view, setView] = useState<CarChangesView | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open || view !== null || carId === undefined || carId === null) return;
    let cancelled = false;
    setError(null);
    fetchCarChanges(carId)
      .then((v) => {
        if (!cancelled) setView(v);
      })
      .catch((e) => {
        if (!cancelled) setError(toErrorMessage(e));
      });
    return () => {
      cancelled = true;
    };
  }, [open, view, carId]);

  if (!r) return null;
  const items = view?.items ?? [];

  return (
    <SectionCard title="Historia zmian z TMS">
      <Button size="small" aria-expanded={open} onClick={() => setOpen((o) => !o)}>
        {open ? 'Zwiń' : 'Pokaż'}
      </Button>
      {open && (
        <Box sx={{ mt: 1 }} data-testid="car-change-history">
          {error && (
            <Typography variant="body2" color="error">
              {error}
            </Typography>
          )}
          {!error && view === null && (
            <Typography variant="body2" color="text.secondary">
              Wczytywanie…
            </Typography>
          )}
          {view !== null && items.length === 0 && (
            <Typography variant="body2">
              Bez zmian od 12.10.2026 — wcześniej import nie zapisywał historii.
            </Typography>
          )}
          {items.length > 0 && (
            <Box sx={{ overflowX: 'auto' }}>
              <Table size="small" aria-label="Historia zmian pojazdu">
                <TableHead>
                  <TableRow>
                    <TableCell>Kiedy</TableCell>
                    <TableCell>Pole</TableCell>
                    <TableCell>Było</TableCell>
                    <TableCell>Jest</TableCell>
                    <TableCell>Źródło</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {items.map((c) => (
                    <TableRow key={c.id}>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>{fmtDate(c.changedAt)}</TableCell>
                      <TableCell>{carChangeFieldLabel(c.field)}</TableCell>
                      <TableCell>{carChangeValueText(c.field, c.oldValue)}</TableCell>
                      <TableCell>{carChangeValueText(c.field, c.newValue)}</TableCell>
                      <TableCell>
                        {carChangeSourceLabel(c.source)}
                        {c.changedByLogin ? ` (${c.changedByLogin})` : ''}
                        {c.reason ? (
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                            {c.reason}
                          </Typography>
                        ) : null}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          )}
          {view !== null && view.limit > 0 && items.length >= view.limit && (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              Pokazano {view.limit} najnowszych zmian.
            </Typography>
          )}
        </Box>
      )}
    </SectionCard>
  );
};
